import axios, { AxiosInstance } from "axios";
import { UserService } from "../../../types/userService";

/**
 * Real API implementation for user service
 */
class RealUserService implements UserService {
  private api: AxiosInstance;
  private getToken: () => string | null;

  constructor(baseURL: string, getToken: () => string | null) {
    this.getToken = getToken;
    this.api = axios.create({
      baseURL: baseURL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.api.interceptors.request.use((config) => {
      const token = this.getToken();
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    });
  }

  /**
   * Get current user profile
   */
  async fetchUserProfile(): Promise<any> {
    try {
      const response = await this.api.get("/users/profile");
      return response.data;
    } catch (error) {
      console.error("Fetch profile error:", error);
      throw error;
    }
  }

  /**
   * Update user profile (name only)
   */
  async updateUserProfile(name: string): Promise<any> {
    try {
      const response = await this.api.put("/users/profile", { name });
      return response.data;
    } catch (error) {
      console.error("Update profile error:", error);
      throw error;
    }
  }

  /**
   * Update user email (requires verification)
   */
  async updateUserEmail(newEmail: string): Promise<any> {
    try {
      const response = await this.api.post("/users/profile/email", {
        new_email: newEmail,
      });
      return response.data;
    } catch (error) {
      console.error("Update email error:", error);
      throw error;
    }
  }

  /**
   * Change user password
   */
  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<any> {
    try {
      const response = await this.api.post("/users/profile/password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      return response.data;
    } catch (error) {
      console.error("Change password error:", error);
      throw error;
    }
  }

  /**
   * Create a checkout session for a plan type (maps plan -> price id)
   */
  async createCheckoutSession(
    planType: string | undefined | null,
    userEmail: string,
  ): Promise<{ url: string; session_id: string }> {
    try {
      const effectivePlan = planType || "monthly_professional";
      console.warn(
        `createCheckoutSession: invalid planType '${planType}', falling back to 'monthly_professional'`,
      );
      const response = await this.api.post("/users/create-checkout-session", {
        email: userEmail,
        tier: effectivePlan,
      });
      return response.data;
    } catch (error) {
      console.error("createCheckoutSession error:", error);
      throw error;
    }
  }

  async createCheckoutSessionWithOptions(
    planType: string | undefined | null,
    payload: {
      userEmail: string;
      coupon?: string;
      billingRequired?: boolean;
      automaticTax?: boolean;
    },
  ): Promise<{ url: string; session_id: string }> {
    try {
      const effectivePlan = planType || "monthly_professional";
      const body: any = { email: payload.userEmail, tier: effectivePlan };
      if (payload.coupon) body.coupon = payload.coupon;
      if (payload.billingRequired) body.billing_required = true;
      if (payload.automaticTax) body.automatic_tax = true;
      const response = await this.api.post(
        "/users/create-checkout-session",
        body,
      );
      return response.data;
    } catch (error) {
      console.error("createCheckoutSessionWithOptions error:", error);
      throw error;
    }
  }

  async getSubscriptionStatus(): Promise<any> {
    try {
      const response = await this.api.get("/users/subscription/status");
      return response.data;
    } catch (error) {
      console.error("getSubscriptionStatus error:", error);
      throw error;
    }
  }

  // Compatibility alias
  async fetchSubscriptionStatus(): Promise<any> {
    return this.getSubscriptionStatus();
  }

  async createPortalSession(): Promise<any> {
    try {
      const response = await this.api.post("/users/subscription/portal");
      return response.data;
    } catch (error) {
      console.error("createPortalSession error:", error);
      throw error;
    }
  }

  async getHasStripeCustomer(): Promise<{ has_customer: boolean }> {
    try {
      const response = await this.api.get("/users/stripe-customer");
      return response.data;
    } catch (error) {
      console.error("getHasStripeCustomer error:", error);
      // Default to false on error
      return { has_customer: false };
    }
  }

  async createSubscriptionCheckout(
    tierId: string,
    payload?: {
      coupon?: string;
      billingRequired?: boolean;
      automaticTax?: boolean;
    },
  ): Promise<{ url: string; session_id?: string }> {
    try {
      const body: any = { tier_id: tierId };
      if (payload?.coupon) body.coupon = payload.coupon;
      if (payload?.billingRequired)
        body.billing_required = payload.billingRequired;
      if (payload?.automaticTax) body.automatic_tax = payload.automaticTax;
      const response = await this.api.post(
        "/users/subscriptions/checkout",
        body,
      );
      return response.data;
    } catch (error) {
      console.error("createSubscriptionCheckout error:", error);
      throw error;
    }
  }

  async verifySubscription(): Promise<{
    verified: boolean;
    status: string;
    tier?: string;
    source: string;
    has_active_subscription: boolean;
  }> {
    try {
      const response = await this.api.get("/users/subscription/verify");
      return response.data;
    } catch (error) {
      console.error("verifySubscription error:", error);
      throw error;
    }
  }

  async validateOrRecoverSubscription(): Promise<{
    can_subscribe: boolean;
    has_active: boolean;
    recovered: boolean;
    customer_id: string | null;
    source: string;
  }> {
    try {
      const response = await this.api.post(
        "/users/subscription/validate-or-recover",
      );
      return response.data;
    } catch (error) {
      console.error("validateOrRecoverSubscription error:", error);
      throw error;
    }
  }
}

export default RealUserService;
